/**
 * Spartan Fitness — Dealer Locator JavaScript
 * Handles pagination, search/filter, province dropdown, 3-view toggle,
 * geolocation sorting, card↔marker highlighting, and Leaflet/OpenStreetMap.
 * No API key required.
 *
 * Ported from the Synergy Bikes dealer-locator.js and adapted for Spartan Fitness.
 */

class SpartanDealerLocator {
    constructor(sectionId) {
        this.sectionId = sectionId;
        this.data = window.dealerLocatorData[sectionId];
        this.dealers = this.data.dealers;
        this.settings = this.data.settings;
        this.currentPage = 1;
        this.filteredDealers = [...this.dealers];
        this.map = null;
        this.markers = [];
        this.markerGroup = null;
        this.userLocation = null;
        this.activeProvinceFilter = '';
        this.currentView = 'both'; // 'map' | 'both' | 'list'

        // Province name aliases (Canada)
        this.provinceAliases = {
            'ab': ['alberta', 'ab'],
            'bc': ['british columbia', 'bc', 'b.c.'],
            'mb': ['manitoba', 'mb'],
            'nb': ['new brunswick', 'nb'],
            'nl': ['newfoundland', 'newfoundland and labrador', 'nl', 'nfld'],
            'ns': ['nova scotia', 'ns'],
            'nt': ['northwest territories', 'nt', 'nwt'],
            'nu': ['nunavut', 'nu'],
            'on': ['ontario', 'on', 'ont'],
            'pe': ['prince edward island', 'pe', 'pei'],
            'qc': ['quebec', 'qc', 'que'],
            'sk': ['saskatchewan', 'sk', 'sask'],
            'yt': ['yukon', 'yt']
        };

        this.regionAliases = {
            'east coast': ['ns', 'nb', 'pe', 'nl'],
            'eastern canada': ['ns', 'nb', 'pe', 'nl', 'on', 'qc'],
            'west coast': ['bc'],
            'western canada': ['bc', 'ab', 'sk', 'mb'],
            'prairies': ['ab', 'sk', 'mb'],
            'atlantic': ['ns', 'nb', 'pe', 'nl'],
            'maritimes': ['ns', 'nb', 'pe'],
            'gta': ['on']
        };

        this.init();
    }

    init() {
        this.cacheElements();
        this.bindEvents();
        this.detectUserLocation();
        this.initPagination();
        this.initMap();
    }

    cacheElements() {
        this.searchInput = document.querySelector('[data-dealer-search]');
        this.searchClearBtn = document.querySelector('[data-search-clear]');
        this.provinceSelect = document.querySelector('[data-province-filter]');
        this.dealersGrid = document.querySelector('[data-dealers-grid]');
        this.dealerCards = this.dealersGrid
            ? Array.from(this.dealersGrid.querySelectorAll('.dealer-card'))
            : [];
        this.dealersWrapper = document.querySelector('.dealer-locator__dealers');
        this.contentWrapper = document.querySelector('.dealer-locator__content');
        this.noResultsEl = document.querySelector('[data-no-results]');
        this.paginationPrev = document.querySelector('[data-pagination-prev]');
        this.paginationNext = document.querySelector('[data-pagination-next]');
        this.paginationPages = Array.from(document.querySelectorAll('[data-pagination-page]'));
        this.currentPageEl = document.querySelector('[data-current-page]');
        this.mapContainer = document.querySelector('[data-dealer-map]');
        this.mapWrapper = document.querySelector('.dealer-locator__map-wrapper');
        this.mapLoading = document.querySelector('.dealer-locator__map-loading');
        this.toggleBtns = document.querySelectorAll('[data-toggle-view]');
    }

    bindEvents() {
        var self = this;

        if (this.searchInput) {
            this.searchInput.addEventListener('input', this.handleSearch.bind(this));
        }
        if (this.searchClearBtn) {
            this.searchClearBtn.addEventListener('click', this.clearSearch.bind(this));
        }

        if (this.provinceSelect) {
            this.provinceSelect.addEventListener('change', function () {
                self.activeProvinceFilter = this.value.toLowerCase();
                self.applyFilters();
            });
        }

        this.toggleBtns.forEach(function (btn) {
            btn.addEventListener('click', function () {
                self.toggleView(this.dataset.toggleView);
            });
        });

        if (this.paginationPrev) {
            this.paginationPrev.addEventListener('click', function () {
                self.goToPage(self.currentPage - 1);
            });
        }
        if (this.paginationNext) {
            this.paginationNext.addEventListener('click', function () {
                self.goToPage(self.currentPage + 1);
            });
        }
        this.paginationPages.forEach(function (btn) {
            btn.addEventListener('click', function (e) {
                self.goToPage(parseInt(e.target.dataset.paginationPage));
            });
        });

        this.dealerCards.forEach(function (card) {
            card.addEventListener('click', function (e) {
                if (e.target.closest('a')) return;
                self.highlightMarkerFromCard(card);
            });
        });
    }

    // ============================================
    // View Toggle — map / both / list
    // ============================================
    toggleView(view) {
        this.currentView = view;

        this.toggleBtns.forEach(function (btn) {
            if (btn.dataset.toggleView === view) {
                btn.classList.add('dealer-locator__toggle-btn--active');
            } else {
                btn.classList.remove('dealer-locator__toggle-btn--active');
            }
        });

        if (this.mapWrapper) this.mapWrapper.classList.remove('dealer-locator__map-wrapper--hidden');
        if (this.dealersWrapper) this.dealersWrapper.classList.remove('dealer-locator__dealers--hidden');
        if (this.contentWrapper) {
            this.contentWrapper.classList.remove('dealer-locator__content--map-only');
            this.contentWrapper.classList.remove('dealer-locator__content--list-only');
        }

        if (view === 'map') {
            if (this.dealersWrapper) this.dealersWrapper.classList.add('dealer-locator__dealers--hidden');
            if (this.contentWrapper) this.contentWrapper.classList.add('dealer-locator__content--map-only');
        } else if (view === 'list') {
            if (this.mapWrapper) this.mapWrapper.classList.add('dealer-locator__map-wrapper--hidden');
            if (this.contentWrapper) this.contentWrapper.classList.add('dealer-locator__content--list-only');
        }

        if (view !== 'list' && this.map) {
            var self = this;
            setTimeout(function () {
                self.map.invalidateSize();
                if (self.markerGroup && self.markerGroup.getLayers().length > 0) {
                    self.map.fitBounds(self.markerGroup.getBounds().pad(0.08));
                }
            }, 180);
        }
    }

    // ============================================
    // Geolocation
    // ============================================
    detectUserLocation() {
        var self = this;

        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                function (position) {
                    self.userLocation = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    };
                    self.onLocationDetected();
                },
                function () { self.fallbackIPLocation(); },
                { timeout: 8000, maximumAge: 300000 }
            );
        } else {
            self.fallbackIPLocation();
        }
    }

    fallbackIPLocation() {
        var self = this;
        fetch('https://ipapi.co/json/')
            .then(function (res) { return res.json(); })
            .then(function (data) {
                if (data.latitude && data.longitude) {
                    self.userLocation = { lat: data.latitude, lng: data.longitude };
                    self.onLocationDetected();
                }
            })
            .catch(function () { /* silent fail */ });
    }

    onLocationDetected() {
        this.updateDistanceBadges();
        this.sortCardsByDistance();
        this.currentPage = 1;
        this.updateDisplay();
        this.updateMapMarkers();
    }

    updateDistanceBadges() {
        if (!this.userLocation) return;
        var self = this;

        this.dealerCards.forEach(function (card) {
            var lat = parseFloat(card.dataset.dealerLat);
            var lng = parseFloat(card.dataset.dealerLng);
            var badge = card.querySelector('[data-dealer-distance]');

            if (badge && !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                var dist = self.haversineDistance(self.userLocation.lat, self.userLocation.lng, lat, lng);
                card._dealerDistance = dist;

                if (dist < 1) {
                    badge.textContent = (dist * 1000).toFixed(0) + ' m';
                } else if (dist < 100) {
                    badge.textContent = dist.toFixed(0) + ' km';
                } else {
                    badge.textContent = Math.round(dist) + ' km';
                }
                badge.style.display = '';
            } else {
                card._dealerDistance = 999999;
            }
        });
    }

    sortCardsByDistance() {
        if (!this.dealersGrid || !this.userLocation) return;

        var sortedCards = this.dealerCards.slice().sort(function (a, b) {
            return (a._dealerDistance || 999999) - (b._dealerDistance || 999999);
        });

        var fragment = document.createDocumentFragment();
        sortedCards.forEach(function (card, i) {
            card.dataset.dealerIndex = i;
            fragment.appendChild(card);
        });
        this.dealersGrid.appendChild(fragment);
        this.dealerCards = sortedCards;

        var self = this;
        this.dealers.sort(function (a, b) {
            var distA = (a.location.lat !== 0 && a.location.lng !== 0)
                ? self.haversineDistance(self.userLocation.lat, self.userLocation.lng, a.location.lat, a.location.lng)
                : 999999;
            var distB = (b.location.lat !== 0 && b.location.lng !== 0)
                ? self.haversineDistance(self.userLocation.lat, self.userLocation.lng, b.location.lat, b.location.lng)
                : 999999;
            return distA - distB;
        });
        this.filteredDealers = [...this.dealers];
    }

    haversineDistance(lat1, lon1, lat2, lon2) {
        var R = 6371;
        var dLat = (lat2 - lat1) * Math.PI / 180;
        var dLon = (lon2 - lon1) * Math.PI / 180;
        var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
        return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    // ============================================
    // Search + Province Filter
    // ============================================
    handleSearch(e) {
        var query = e.target.value.toLowerCase().trim();
        if (this.searchClearBtn) {
            this.searchClearBtn.style.display = query ? 'flex' : 'none';
        }
        this.applyFilters();
    }

    applyFilters() {
        var query = this.searchInput ? this.searchInput.value.toLowerCase().trim() : '';
        var provinceDrop = this.activeProvinceFilter;
        var self = this;
        var matchingProvinceCodes = query ? this.resolveProvinceSearch(query) : [];

        this.filteredDealers = this.dealers.filter(function (dealer) {
            if (provinceDrop) {
                var dealerProv = (dealer.address.province || '').toLowerCase().trim();
                if (dealerProv !== provinceDrop) return false;
            }

            if (query) {
                var searchableText = [
                    dealer.name,
                    dealer.address.street,
                    dealer.address.city,
                    dealer.address.province,
                    dealer.address.country
                ].filter(Boolean).join(' ').toLowerCase();

                if (searchableText.includes(query)) return true;

                if (matchingProvinceCodes.length > 0) {
                    var dp = (dealer.address.province || '').toLowerCase().trim();
                    return matchingProvinceCodes.includes(dp);
                }

                return false;
            }

            return true;
        });

        this.currentPage = 1;
        this.updateDisplay();
        this.updateMapMarkers();
    }

    resolveProvinceSearch(query) {
        var matchedCodes = [];
        var self = this;

        Object.keys(this.regionAliases).forEach(function (region) {
            if (region.includes(query) || query.includes(region)) {
                self.regionAliases[region].forEach(function (code) {
                    if (matchedCodes.indexOf(code) === -1) matchedCodes.push(code);
                });
            }
        });

        Object.keys(this.provinceAliases).forEach(function (code) {
            var aliases = self.provinceAliases[code];
            for (var i = 0; i < aliases.length; i++) {
                if (aliases[i].includes(query) || query.includes(aliases[i])) {
                    if (matchedCodes.indexOf(code) === -1) matchedCodes.push(code);
                    break;
                }
            }
        });

        return matchedCodes;
    }

    clearSearch() {
        if (this.searchInput) this.searchInput.value = '';
        if (this.searchClearBtn) this.searchClearBtn.style.display = 'none';
        this.applyFilters();
        if (this.searchInput) this.searchInput.focus();
    }

    // ============================================
    // Pagination
    // ============================================
    initPagination() { this.updateDisplay(); }

    goToPage(page) {
        var totalPages = Math.ceil(this.filteredDealers.length / this.settings.itemsPerPage);
        if (page < 1 || page > totalPages) return;

        this.currentPage = page;
        this.updateDisplay();

        if (this.dealersGrid) {
            var offsetTop = this.dealersGrid.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: offsetTop, behavior: 'smooth' });
        }
    }

    updateDisplay() {
        var itemsPerPage = this.settings.itemsPerPage;
        var startIndex = (this.currentPage - 1) * itemsPerPage;
        var endIndex = startIndex + itemsPerPage;
        var totalPages = Math.ceil(this.filteredDealers.length / itemsPerPage);
        var self = this;

        this.dealerCards.forEach(function (card) {
            var dealerIndex = parseInt(card.dataset.dealerIndex);
            var dealer = self.dealers[dealerIndex];
            var filteredIndex = self.filteredDealers.indexOf(dealer);
            var isInPage = filteredIndex >= startIndex && filteredIndex < endIndex;

            if (filteredIndex > -1 && isInPage) {
                card.classList.remove('hidden');
            } else {
                card.classList.add('hidden');
            }
        });

        if (this.noResultsEl) {
            this.noResultsEl.style.display = this.filteredDealers.length === 0 ? 'block' : 'none';
        }

        this.updatePaginationControls(totalPages);
    }

    updatePaginationControls(totalPages) {
        var self = this;

        if (this.paginationPrev) {
            this.paginationPrev.disabled = this.currentPage === 1;
        }
        if (this.paginationNext) {
            this.paginationNext.disabled = this.currentPage === totalPages || totalPages === 0;
        }

        this.paginationPages.forEach(function (btn) {
            var page = parseInt(btn.dataset.paginationPage);
            if (page === self.currentPage) {
                btn.classList.add('pagination-controls__page--active');
                btn.setAttribute('aria-current', 'page');
            } else {
                btn.classList.remove('pagination-controls__page--active');
                btn.removeAttribute('aria-current');
            }
            btn.style.display = page > totalPages ? 'none' : '';
        });

        if (this.currentPageEl) {
            this.currentPageEl.textContent = this.currentPage;
        }
    }

    // ============================================
    // Map → Card highlight
    // ============================================
    highlightCard(dealer) {
        var self = this;
        this.dealerCards.forEach(function (c) { c.classList.remove('dealer-card--highlight'); });

        var targetCard = null;
        for (var i = 0; i < this.dealerCards.length; i++) {
            var card = this.dealerCards[i];
            var lat = parseFloat(card.dataset.dealerLat);
            var lng = parseFloat(card.dataset.dealerLng);
            if (Math.abs(lat - dealer.location.lat) < 0.0001 && Math.abs(lng - dealer.location.lng) < 0.0001) {
                targetCard = card;
                break;
            }
        }

        if (!targetCard) return;

        if (this.currentView === 'map') this.toggleView('both');

        var dealerIndex = parseInt(targetCard.dataset.dealerIndex);
        var dealerObj = this.dealers[dealerIndex];
        var filteredIdx = this.filteredDealers.indexOf(dealerObj);
        if (filteredIdx > -1) {
            var neededPage = Math.floor(filteredIdx / this.settings.itemsPerPage) + 1;
            if (neededPage !== this.currentPage) {
                this.currentPage = neededPage;
                this.updateDisplay();
            }
        }

        targetCard.classList.add('dealer-card--highlight');
        setTimeout(function () { targetCard.classList.remove('dealer-card--highlight'); }, 3000);
    }

    // ============================================
    // Card → Marker highlight
    // ============================================
    highlightMarkerFromCard(card) {
        var lat = parseFloat(card.dataset.dealerLat);
        var lng = parseFloat(card.dataset.dealerLng);
        if (isNaN(lat) || isNaN(lng)) return;

        if (this.currentView === 'list') this.toggleView('both');

        var targetMarker = null;
        for (var i = 0; i < this.markers.length; i++) {
            var markerLatLng = this.markers[i].getLatLng();
            if (Math.abs(markerLatLng.lat - lat) < 0.0001 && Math.abs(markerLatLng.lng - lng) < 0.0001) {
                targetMarker = this.markers[i];
                break;
            }
        }

        if (!targetMarker || !this.map) return;
        targetMarker.openPopup();

        var markerEl = targetMarker.getElement();
        if (markerEl) {
            markerEl.classList.add('dealer-map-marker--pulse');
            setTimeout(function () { markerEl.classList.remove('dealer-map-marker--pulse'); }, 2500);
        }

        var self = this;
        this.dealerCards.forEach(function (c) { c.classList.remove('dealer-card--highlight'); });
        card.classList.add('dealer-card--highlight');
        setTimeout(function () { card.classList.remove('dealer-card--highlight'); }, 2500);
    }

    // ============================================
    // Leaflet / OpenStreetMap
    // ============================================
    initMap() {
        if (!this.mapContainer) {
            if (this.mapLoading) this.mapLoading.classList.add('hidden');
            return;
        }

        if (typeof L === 'undefined') {
            console.error('Leaflet not loaded');
            if (this.mapLoading) this.mapLoading.innerHTML = '<p style="color:#d34a31;">Map library failed to load.</p>';
            return;
        }

        var validDealers = this.dealers.filter(function (d) {
            return d.location.lat !== 0 && d.location.lng !== 0;
        });

        if (validDealers.length === 0) {
            if (this.mapLoading) this.mapLoading.innerHTML = '<p>No dealer locations with coordinates found.</p>';
            return;
        }

        if (this.mapLoading) this.mapLoading.classList.add('hidden');

        this.map = L.map(this.mapContainer, { scrollWheelZoom: false });

        L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            maxZoom: 19
        }).addTo(this.map);

        this.markerGroup = L.featureGroup();

        var defaultIcon = L.divIcon({
            className: 'dealer-map-marker',
            html: '<div class="dealer-map-marker__dot"></div>',
            iconSize: [20, 20],
            iconAnchor: [10, 10],
            popupAnchor: [0, -12]
        });

        var featuredIcon = L.divIcon({
            className: 'dealer-map-marker dealer-map-marker--featured',
            html: '<div class="dealer-map-marker__dot dealer-map-marker__dot--featured"></div>',
            iconSize: [24, 24],
            iconAnchor: [12, 12],
            popupAnchor: [0, -14]
        });

        var self = this;
        validDealers.forEach(function (dealer) {
            var icon = dealer.featured ? featuredIcon : defaultIcon;
            var marker = L.marker([dealer.location.lat, dealer.location.lng], { icon: icon });

            // Build full address string for accurate Directions routing
            var addrParts = [];
            if (dealer.address.street) addrParts.push(dealer.address.street);
            if (dealer.address.city) addrParts.push(dealer.address.city);
            if (dealer.address.province) addrParts.push(dealer.address.province);
            if (dealer.address.postal) addrParts.push(dealer.address.postal);
            if (dealer.address.country) addrParts.push(dealer.address.country);
            var addrEncoded = encodeURIComponent(addrParts.join(', '));

            // Business-name search: "Spartan Fitness Equipment Burlington ON"
            // This opens the Google Business Profile instead of a generic address pin
            var bizParts = ['Spartan Fitness Commercial'];
            if (dealer.address.city) bizParts.push(dealer.address.city);
            if (dealer.address.province) bizParts.push(dealer.address.province);
            var bizEncoded = encodeURIComponent(bizParts.join(' '));

            var popupHtml = '<div style="min-width:180px;padding:4px;">' +
                '<h4 style="margin:0 0 6px;font-size:14px;font-weight:700;color:#f4f4f6;text-transform:uppercase;">' + dealer.name + '</h4>' +
                '<p style="margin:0 0 4px;font-size:12px;line-height:1.4;color:#a9a9b0;">' +
                (dealer.address.street ? dealer.address.street + '<br>' : '') +
                dealer.address.city + ', ' + dealer.address.province +
                (dealer.address.postal ? ' ' + dealer.address.postal : '') +
                '</p>' +
                (dealer.phone ? '<p style="margin:4px 0;font-size:12px;color:#a9a9b0;">' + dealer.phone + '</p>' : '') +
                '<div style="display:flex;gap:12px;margin-top:8px;flex-wrap:wrap;">' +
                '<a href="https://www.google.com/maps/dir/?api=1&destination=' + addrEncoded + '" ' +
                'target="_blank" rel="noopener" style="color:#e9e9ee;text-decoration:none;font-size:12px;font-weight:600;">Directions &rarr;</a>' +
                '<a href="https://www.google.com/maps/search/?api=1&query=' + bizEncoded + '" ' +
                'target="_blank" rel="noopener" style="color:#e9e9ee;text-decoration:none;font-size:12px;font-weight:600;">View on Maps &rarr;</a>' +
                (dealer.website ? '<a href="' + dealer.website + '" target="_blank" rel="noopener" ' +
                    'style="color:#e9e9ee;text-decoration:none;font-size:12px;font-weight:600;">Website &rarr;</a>' : '') +
                '</div></div>';

            marker.bindPopup(popupHtml);

            marker.on('mouseover', function () { this.openPopup(); });
            marker.on('mouseout', function () {
                var m = this;
                setTimeout(function () {
                    var popupEl = m.getPopup().getElement();
                    if (!popupEl || !popupEl.matches(':hover')) m.closePopup();
                }, 400);
            });

            marker.on('click', function () { self.highlightCard(dealer); });

            marker.dealerRef = dealer;
            self.markers.push(marker);
            self.markerGroup.addLayer(marker);
        });

        this.markerGroup.addTo(this.map);

        // Default to Canada-centred view so pins are clearly visible on load
        this.map.setView([56.1304, -106.3468], 4);

        this.map.once('click', function () { self.map.scrollWheelZoom.enable(); });
    }

    updateMapMarkers() {
        if (!this.map || !this.markerGroup) return;

        var self = this;
        var hasVisible = false;
        var bounds = L.latLngBounds();

        this.markers.forEach(function (marker) {
            var isVisible = self.filteredDealers.includes(marker.dealerRef);
            if (isVisible) {
                if (!self.markerGroup.hasLayer(marker)) self.markerGroup.addLayer(marker);
                bounds.extend(marker.getLatLng());
                hasVisible = true;
            } else {
                self.markerGroup.removeLayer(marker);
            }
        });

        if (hasVisible) this.map.fitBounds(bounds.pad(0.1), { maxZoom: 15 });
    }
}

// ---- Initialize ----
(function () {
    function initAllDealerLocators() {
        if (window.dealerLocatorData) {
            var keys = Object.keys(window.dealerLocatorData);
            for (var i = 0; i < keys.length; i++) {
                try {
                    new SpartanDealerLocator(keys[i]);
                } catch (e) {
                    console.error('Spartan Dealer Locator init error:', e);
                }
            }
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAllDealerLocators);
    } else {
        initAllDealerLocators();
    }
})();
